import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip auth check entirely on truly public pages — avoids needless 401s
    // in console. The /race page IS public, but we still try the auth check
    // there so logged-in admins see their inline "Hide" controls. Non-logged
    // viewers will get a single silent 401 and we continue as anonymous.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.startsWith("/join/")) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("cfn_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("cfn_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout is best-effort: if the server call fails (network, expired
      // session, etc.) we still clear local state so the user is logged out.
      console.warn("Logout request failed (clearing local session anyway):", e?.message || e);
    }
    localStorage.removeItem("cfn_token");
    setUser(false);
  };

  const setUserAndToken = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: checkAuth, setUserAndToken, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
